import { PuzzleLoader } from './puzzle-loader.js';
import { deepDiveRouter } from './deepDiveRouterInstance.js';
import { instrumentation } from './instrumentation.js';

export class TaxonomicGame {
    constructor() {
        this.state = {
            currentPuzzle: null,
            puzzleDate: null,
            mode: 'main', // 'main' or 'deepdive'
            selectedWords: [],
            pairedWords: [],
            completedCategories: [],
            deepDiveContext: null,
            shuffledWords: [],
            stackedPairs: {} // { categoryId: { pairs: [[idx1,idx2]], topWord: 'text', complete: false } }
        };
    }

    /**
     * Initialize game with puzzle
     */
    async init() {
        const { puzzle, date } = await PuzzleLoader.loadDailyPuzzle();
        this.state.currentPuzzle = puzzle;
        this.state.puzzleDate = date;
        this.state.shuffledWords = this.shuffleMainBoard(puzzle);
        return this.state;
    }

    /**
     * Shuffle all words from all categories into random order
     */
    shuffleMainBoard(puzzle) {
        const allWords = [];
        puzzle.categories.forEach((category, categoryIndex) => {
            category.words.forEach(word => {
                allWords.push({
                    ...word,
                    categoryId: category.id,
                    categoryIndex: categoryIndex
                });
            });
        });

        // Fisher-Yates shuffle
        for (let i = allWords.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [allWords[i], allWords[j]] = [allWords[j], allWords[i]];
        }

        return allWords;
    }

    /**
     * Select a word
     */
    selectWord(wordIndex) {
        const word = this.getCurrentWords()[wordIndex];
        if (!word) return false;

        // Check if word is already paired
        const isPaired = this.isWordPaired(wordIndex);

        if (isPaired) {
            // In deep dive mode, don't allow selecting already-paired words
            if (this.state.mode === 'deepdive') {
                return { action: 'already_paired' };
            }
            return this.selectPair(wordIndex);
        } else {
            return this.selectSingleWord(wordIndex);
        }
    }

    /**
     * Check if word is part of a pair
     */
    isWordPaired(wordIndex) {
        if (this.state.mode === 'deepdive') {
            // In deep dive, check deep dive pairs
            return this.state.deepDiveContext.pairedIndices.some(pair =>
                pair.includes(wordIndex)
            );
        } else {
            // In main mode, check main board pairs
            return this.state.pairedWords.some(pair =>
                pair.includes(wordIndex)
            );
        }
    }

    /**
     * Select a single unpaired word
     */
    selectSingleWord(wordIndex) {
        // If already selected, deselect
        if (this.state.selectedWords.includes(wordIndex)) {
            this.state.selectedWords = this.state.selectedWords.filter(i => i !== wordIndex);
            return { action: 'deselect' };
        }

        // Can only select 2 unpaired words at once
        if (this.state.selectedWords.length >= 2) {
            this.state.selectedWords.shift(); // Remove first selection
        }

        this.state.selectedWords.push(wordIndex);

        // Check if we have 2 selected words
        if (this.state.selectedWords.length === 2) {
            return this.checkPairMatch();
        }

        return { action: 'select' };
    }

    /**
     * Select a paired word (selects entire pair)
     * In main mode, paired words are not selectable (category auto-completes)
     * This function is only used in deep dive mode now
     */
    selectPair(wordIndex) {
        // In main mode, don't allow selecting paired words (they're complete)
        if (this.state.mode === 'main') {
            return { action: 'already_paired' };
        }

        // Deep dive mode - this shouldn't be called due to earlier check
        // but keeping for safety
        return { action: 'already_paired' };
    }

    /**
     * Check if 2 selected words form a valid pair
     */
    checkPairMatch() {
        const words = this.getCurrentWords();
        const [idx1, idx2] = this.state.selectedWords;
        const word1 = words[idx1];
        const word2 = words[idx2];

        // In main mode, check if same category (any pairing allowed)
        if (this.state.mode === 'main') {
            if (word1.categoryId === word2.categoryId) {
                this.state.pairedWords.push([idx1, idx2]);
                this.state.selectedWords = [];

                // Check if this completes a category (all 4 words paired)
                const categoryId = word1.categoryId;
                const categoryComplete = this.checkCategoryComplete(categoryId);

                if (categoryComplete) {
                    return { action: 'category_complete', categoryId, indices: [idx1, idx2] };
                }

                return { action: 'pair_matched', indices: [idx1, idx2] };
            }
        } else {
            // In deep dive mode, check if they're a valid pair
            const deepDive = this.state.currentPuzzle.categories
                .find(c => c.id === this.state.deepDiveContext.categoryId).deepDive;

            const validPair = deepDive.pairs.find(p =>
                (p.words[0] === word1.text && p.words[1] === word2.text) ||
                (p.words[1] === word1.text && p.words[0] === word2.text)
            );

            if (validPair) {
                // In deep dive, only track in deepDiveContext, not pairedWords
                this.state.deepDiveContext.pairedIndices.push([idx1, idx2]);
                this.state.selectedWords = [];

                // Check if deep dive is complete (4 pairs = 8 words)
                if (this.state.deepDiveContext.pairedIndices.length === 4) {
                    return { action: 'deepdive_complete' };
                }

                return { action: 'pair_matched', indices: [idx1, idx2] };
            }
        }

        // No match - wrong pair
        const wrongIndices = [idx1, idx2];
        this.state.selectedWords = [];
        return { action: 'wrong_pair', indices: wrongIndices };
    }

    /**
     * Check if all words from a category are paired
     */
    checkCategoryComplete(categoryId) {
        const words = this.getCurrentWords();

        // Find all indices of words from this category
        const categoryIndices = [];
        words.forEach((word, idx) => {
            if (word.categoryId === categoryId) {
                categoryIndices.push(idx);
            }
        });

        // Get the expected word count for this specific category
        const category = this.state.currentPuzzle.categories.find(c => c.id === categoryId);
        if (!category || categoryIndices.length !== category.words.length) return false;

        // Flatten all paired word indices
        const pairedIndices = this.state.pairedWords.flat();

        // Check if all category words are in paired indices
        const allPaired = categoryIndices.every(idx => pairedIndices.includes(idx));

        // Track category completion
        if (allPaired) {
            instrumentation?.trackCategoryCompleted(
                categoryId,
                category.name,
                this.state.completedCategories.length + 1,
                this.state.currentPuzzle.categories.length
            );
        }

        return allPaired;
    }

    /**
     * Check if 4 selected words (2 pairs) form a category
     */
    checkCategoryMatch() {
        const words = this.getCurrentWords();
        const selectedIndices = this.state.selectedWords;
        const categoryId = words[selectedIndices[0]].categoryId;

        // Check if all 4 words belong to same category
        const allSameCategory = selectedIndices.every(idx =>
            words[idx].categoryId === categoryId
        );

        if (allSameCategory) {
            // Found a category!
            this.state.completedCategories.push(categoryId);
            this.state.selectedWords = [];

            // Enter deep dive
            return this.enterDeepDive(categoryId);
        }

        // No match
        const wrongIndices = [...selectedIndices];
        this.state.selectedWords = [];
        return { action: 'wrong_category', indices: wrongIndices };
    }

    /**
     * Enter deep dive mode
     */
    enterDeepDive(categoryId, onComplete = null) {
        try {
            const category = this.state.currentPuzzle.categories.find(c => c.id === categoryId);

            // Save main board state before entering deep dive
            this.state.mainBoardState = {
                shuffledWords: [...this.state.shuffledWords],
                pairedWords: [...this.state.pairedWords]
            };

            // Load mini-game for this category using the router
            const miniGame = deepDiveRouter.loadMiniGame(category, onComplete);

            this.state.mode = 'deepdive';
            this.state.deepDiveContext = {
                categoryId: categoryId,
                categoryAnswer: category.answer,
                categoryType: category.categoryType,
                mechanic: category.deepDive.mechanic,
                miniGame: miniGame
            };

            // Track deep dive entry
            instrumentation?.trackDeepDiveEntered(
                categoryId,
                category.name,
                category.deepDive.mechanic
            );

            return {
                action: 'enter_deepdive',
                categoryId: categoryId,
                categoryAnswer: category.answer
            };
        } catch (error) {
            instrumentation?.trackError(error, {
                operation: 'enterDeepDive',
                categoryId: categoryId
            });
            throw error;
        }
    }

    /**
     * Exit deep dive back to main board
     */
    exitDeepDive() {
        // Save categoryId before clearing context
        const exitingCategoryId = this.state.deepDiveContext?.categoryId;

        this.state.mode = 'main';
        this.state.selectedWords = [];
        this.state.deepDiveContext = null;

        // Restore main board state if saved (preserves word positions and incomplete pairs)
        if (this.state.mainBoardState) {
            this.state.shuffledWords = this.state.mainBoardState.shuffledWords;
            this.state.pairedWords = this.state.mainBoardState.pairedWords;

            // Add pairs for the just-completed category
            this.addCompletedCategoryPairs();

            // Clean up saved state
            this.state.mainBoardState = null;
        } else {
            // Fallback: reshuffle and rebuild (shouldn't happen in normal flow)
            this.state.shuffledWords = this.shuffleMainBoard(this.state.currentPuzzle);
            this.rebuildMainBoardPairs();
        }

        // Track deep dive exit
        if (exitingCategoryId) {
            instrumentation?.trackDeepDiveExited(exitingCategoryId, true);
        }

        // Check if game is complete
        if (this.isGameComplete()) {
            // Track game won
            instrumentation?.trackGameWon(
                this.state.puzzleDate,
                this.state.currentPuzzle.categories.length
            );
            return { action: 'game_complete' };
        }

        return { action: 'exit_deepdive' };
    }

    /**
     * Build pairs for completed categories
     * @param {boolean} appendMode - If true, append to existing pairs; if false, replace
     * @returns {Array} The pairs array
     */
    buildCategoryPairs(appendMode = false) {
        const words = this.state.shuffledWords;
        const pairs = appendMode ? [...this.state.pairedWords] : [];

        this.state.completedCategories.forEach(categoryId => {
            const categoryWordIndices = words
                .map((word, idx) => ({ idx, categoryId: word.categoryId }))
                .filter(w => w.categoryId === categoryId)
                .map(w => w.idx);

            const category = this.state.currentPuzzle.categories.find(c => c.id === categoryId);
            const expectedCount = category?.words.length || 0;

            if (categoryWordIndices.length === expectedCount) {
                for (let i = 0; i < categoryWordIndices.length; i += 2) {
                    const pair = [categoryWordIndices[i], categoryWordIndices[i + 1]];

                    if (appendMode) {
                        const pairExists = pairs.some(existingPair =>
                            (existingPair[0] === pair[0] && existingPair[1] === pair[1]) ||
                            (existingPair[0] === pair[1] && existingPair[1] === pair[0])
                        );
                        if (!pairExists) {
                            pairs.push(pair);
                        }
                    } else {
                        pairs.push(pair);
                    }
                }
            }
        });

        return pairs;
    }

    /**
     * Add pairs for completed categories (append mode)
     */
    addCompletedCategoryPairs() {
        this.state.pairedWords = this.buildCategoryPairs(true);
    }

    /**
     * Rebuild pairs from scratch for completed categories
     */
    rebuildMainBoardPairs() {
        this.state.pairedWords = this.buildCategoryPairs(false);
    }

    /**
     * Get current words based on mode
     */
    getCurrentWords() {
        return this.state.shuffledWords;
    }

    /**
     * Get grid size for current puzzle
     */
    getGridSize() {
        return this.state.currentPuzzle.categories.length;
    }

    /**
     * Get total number of words across all categories
     */
    getTotalWords() {
        return this.state.currentPuzzle.categories.reduce((sum, cat) => sum + cat.words.length, 0);
    }

    /**
     * Get visual grid size (square root of total words)
     */
    getVisualGridSize() {
        return Math.sqrt(this.getTotalWords());
    }

    /**
     * Check if game is complete
     */
    isGameComplete() {
        return this.state.completedCategories.length === this.state.currentPuzzle.categories.length;
    }

    /**
     * Add a matched pair to the stack for a category
     * @param {string} categoryId - Category ID
     * @param {number[]} indices - Pair of word indices [idx1, idx2]
     * @param {string} topWord - Text of the most recently matched word (for display)
     */
    addToStack(categoryId, indices, topWord) {
        if (!this.state.stackedPairs[categoryId]) {
            this.state.stackedPairs[categoryId] = {
                pairs: [],
                topWord: topWord,
                complete: false
            };
        }

        this.state.stackedPairs[categoryId].pairs.push(indices);
        this.state.stackedPairs[categoryId].topWord = topWord;
    }

    /**
     * Mark a category stack as complete
     * @param {string} categoryId - Category ID
     */
    markStackComplete(categoryId) {
        if (this.state.stackedPairs[categoryId]) {
            this.state.stackedPairs[categoryId].complete = true;
        }
    }

    /**
     * Get all indices that have been moved to stacks
     * @returns {number[]} Flat array of all stacked word indices
     */
    getStackedIndices() {
        const indices = [];
        Object.values(this.state.stackedPairs).forEach(stack => {
            stack.pairs.forEach(pair => {
                indices.push(...pair);
            });
        });
        return indices;
    }

    /**
     * Get stack data for rendering
     * @returns {Object[]} Array of { categoryId, categoryIndex, pairs, topWord, complete }
     */
    getStacksData() {
        const stacks = [];
        const categories = this.state.currentPuzzle?.categories || [];

        Object.entries(this.state.stackedPairs).forEach(([categoryId, stackData]) => {
            const categoryIndex = categories.findIndex(c => c.id === categoryId);
            const category = categories[categoryIndex];

            stacks.push({
                categoryId,
                categoryIndex,
                categoryName: category?.answer || categoryId,
                pairs: stackData.pairs,
                topWord: stackData.topWord,
                complete: stackData.complete
            });
        });

        // Sort by category index for consistent display order
        stacks.sort((a, b) => a.categoryIndex - b.categoryIndex);

        return stacks;
    }
}
